/**
 * TypeScript types for User Preferences
 * ⚠️ MUST match backend schema: jobai/job_mobile/schemas/preferences.py
 */

import {
  briefcaseOutline,
  locationOutline,
  codeSlashOutline,
  businessOutline,
  cashOutline,
  timeOutline,
  trendingUpOutline,
} from 'ionicons/icons';

// ============================================================================
// Preference Section Types (match backend Pydantic schemas exactly)
// ============================================================================

export interface JobPreferences {
  family?: string;
  roles?: string[]; // ← Backend uses "roles" not "titles"
  seniority?: string; // ← Single string: "junior" | "mid" | "senior" | "lead" | "staff" | "principal"
  search_status?: string; // ← "actively_looking" | "passively_looking" | "not_looking"
  confidence?: number;
}

export interface LocationPreferences {
  remote?: boolean; // ← Backend uses "remote" not "work_from_home"
  hybrid_ok?: boolean; // ← Backend uses "hybrid_ok" not "hybrid"
  countries?: string[];
  cities?: string[];
  time_zones?: string[];
  relocation_willing?: boolean; // ← Backend uses "relocation_willing" not "open_to_relocation"
  confidence?: number;
}

export interface SkillPreferences {
  required?: string[]; // ← Backend uses "required" not "technical_skills"
  nice_to_have?: string[]; // ← Backend uses "nice_to_have" not "soft_skills"
  learning?: string[]; // ← Backend uses "learning" not "tools_technologies"
  expertise_level?: Record<string, string>; // ← "beginner" | "intermediate" | "expert"
  confidence?: number;
}

export interface CompanyPreferences {
  stages?: string[]; // ← Backend uses "stages" (plural) not "stage"
  sizes?: string[]; // ← Backend uses "sizes" (plural) not "size"
  values?: string[]; // ← Backend uses "values" not "company_culture_values"
  confidence?: number;
}

export interface CompensationPreferences {
  currency?: string; // ← Default: "USD"
  min?: number; // ← Backend uses "min" not "salary_min"
  max?: number; // ← Backend uses "max" not "salary_max"
  equity_important?: boolean;
  benefits_priorities?: string[]; // ← Backend uses "benefits_priorities" not "preferred_benefits"
  confidence?: number;
}

export interface WorkStylePreferences {
  work_hours?: string; // ← "flexible" | "standard" | "async"
  team_size?: string; // ← "small" | "medium" | "large"
  autonomy_level?: string; // ← "high" | "medium" | "collaborative"
  meeting_culture?: string; // ← "minimal" | "balanced" | "frequent"
  confidence?: number;
}

export interface CareerGoals {
  short_term?: string;
  long_term?: string;
  growth_areas?: string[];
  confidence?: number;
}

// ============================================================================
// Main Preference Data Structure (matches backend exactly)
// ============================================================================

export interface PreferenceData {
  job?: JobPreferences;
  locations?: LocationPreferences;
  skills?: SkillPreferences;
  industries?: string[]; // ← Top-level array, not under company
  company?: CompanyPreferences;
  compensation?: CompensationPreferences;
  work_style?: WorkStylePreferences;
  career_goals?: CareerGoals;
  notes?: string;
  deal_breakers?: string[];
}

// Backend metadata structure (NOT including onboarding fields)
export interface PreferenceMetadata {
  id: string;
  user_id: string;
  profile_type: string;
  source: string;
  confidence: number;
  version: number;
  created_at: string;
  updated_at: string;
  last_user_review_at?: string | null;
}

// Low confidence section info
export interface LowConfidenceSection {
  section: string;
  confidence: number;
}

// ============================================================================
// Complete User Preferences (matches backend response)
// ============================================================================

export interface UserPreferences {
  metadata: PreferenceMetadata;
  preference_data: PreferenceData;
  // Onboarding fields are at TOP LEVEL, not in metadata!
  low_confidence_sections?: LowConfidenceSection[];
  should_show_onboarding?: boolean;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface PreferenceUpdateRequest {
  job?: Partial<JobPreferences>;
  locations?: Partial<LocationPreferences>;
  skills?: Partial<SkillPreferences>;
  industries?: string[];
  company?: Partial<CompanyPreferences>;
  compensation?: Partial<CompensationPreferences>;
  work_style?: Partial<WorkStylePreferences>;
  career_goals?: Partial<CareerGoals>;
  notes?: string;
  deal_breakers?: string[];
}

export interface PreferenceSectionUpdateRequest {
  data: Record<string, any>;
  merge?: boolean;
}

export interface PreferenceResponse {
  success: boolean;
  message: string;
  data: UserPreferences;
}

// Backend response structure for preference mutations (create/update/confirm)
export interface PreferenceMergeResponse {
  success: boolean;
  message: string;
  updated_preference: UserPreferences;
  merge_summary?: Record<string, any> | null;
}

// ============================================================================
// SSE Event Types
// ============================================================================

export interface PreferenceSSEEvent {
  type: 'preferences_extracted' | 'preferences_updated' | 'preferences_show_onboarding';
  user_id: string;
  overall_confidence?: number;
  should_show_onboarding?: boolean;
  low_confidence_sections?: string[];
  timestamp: string;
  message?: string;
}

// ============================================================================
// UI Helper Types
// ============================================================================

export interface PreferenceSectionConfig {
  key: string;
  title: string;
  icon: any; // Ionicons icon
  emoji: string;
  description: string;
  color: string;
  priority: number;
}

// ⚠️ FIXED: Changed from Record to Array so it can be mapped
// Streamlined to 6 core sections for better UX
export const PREFERENCE_SECTIONS: PreferenceSectionConfig[] = [
  {
    key: 'job',
    title: 'Job Preferences',
    icon: briefcaseOutline,
    emoji: '💼',
    description: 'Your ideal role, seniority, and job family',
    color: '#3880ff',
    priority: 1,
  },
  {
    key: 'locations',
    title: 'Location Preferences',
    icon: locationOutline,
    emoji: '📍',
    description: 'Where you want to work',
    color: '#10dc60',
    priority: 2,
  },
  {
    key: 'compensation',
    title: 'Compensation',
    icon: cashOutline,
    emoji: '💰',
    description: 'Salary expectations and benefits',
    color: '#f4a942',
    priority: 3,
  },
  {
    key: 'industries',
    title: 'Industries',
    icon: businessOutline,
    emoji: '🏢',
    description: 'Industries and fields you want to work in',
    color: '#f04141',
    priority: 4,
  },
  {
    key: 'company',
    title: 'Company Preferences',
    icon: businessOutline,
    emoji: '🏛️',
    description: 'Company stage, size, and culture',
    color: '#7044ff',
    priority: 5,
  },
  {
    key: 'skills',
    title: 'Skills & Expertise',
    icon: codeSlashOutline,
    emoji: '🛠️',
    description: 'Your technical skills and proficiency',
    color: '#ffce00',
    priority: 6,
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

export const getConfidenceColor = (confidence: number): string => {
  if (isNaN(confidence) || confidence === 0) return 'medium'; // Grey for incomplete/initial state
  if (confidence >= 0.8) return 'success'; // Green - excellent
  if (confidence >= 0.6) return 'success'; // Green - good
  if (confidence >= 0.4) return 'warning'; // Orange/yellow - fair
  return 'warning'; // Orange/yellow - needs improvement
};

export const getConfidenceLabel = (confidence: number): string => {
  if (isNaN(confidence) || confidence === 0) return 'Getting Started';
  if (confidence >= 0.8) return 'Excellent';
  if (confidence >= 0.6) return 'Good';
  if (confidence >= 0.4) return 'Fair';
  return 'Needs Improvement';
};

// Labels for editing context (onboarding modal)
export const getConfidenceLabelForEditing = (confidence: number): string => {
  if (isNaN(confidence) || confidence === 0) return 'Please Review';
  if (confidence >= 0.8) return 'Looks Great!';
  if (confidence >= 0.6) return 'Please Verify';
  if (confidence >= 0.4) return 'Please Review';
  return 'Please Complete';
};

// Valid values for dropdowns (matches backend validation)
export const SENIORITY_LEVELS = [
  'junior',
  'mid',
  'senior',
  'lead',
  'staff',
  'principal',
];

export const SEARCH_STATUSES = [
  { value: 'actively_looking', label: 'Actively Looking' },
  { value: 'passively_looking', label: 'Passively Looking' },
  { value: 'not_looking', label: 'Not Looking' },
];

export const WORK_HOURS = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'standard', label: 'Standard' },
  { value: 'async', label: 'Async' },
];

export const TEAM_SIZES = [
  { value: 'small', label: 'Small (1-5)' },
  { value: 'medium', label: 'Medium (6-15)' },
  { value: 'large', label: 'Large (16+)' },
];

export const AUTONOMY_LEVELS = [
  { value: 'high', label: 'High Autonomy' },
  { value: 'medium', label: 'Medium Autonomy' },
  { value: 'collaborative', label: 'Collaborative' },
];

export const MEETING_CULTURES = [
  { value: 'minimal', label: 'Minimal Meetings' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'frequent', label: 'Frequent Meetings' },
];

export const COMPANY_STAGES = [
  'Seed',
  'Series A',
  'Series B',
  'Series C',
  'Unicorn',
  'Public',
];

export const COMPANY_SIZES = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+',
];
